import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col } from 'react-bootstrap';
import find from 'lodash/find';
import { useTranslation } from 'react-i18next';
import {
    GeneralSelect,
    PopOutChart,
    InfoMessage
} from 'sharedComponents';
import { MAX_LOGGER_SIGNALS } from '../constants';
import useChartConfig, { chartPlugins } from './useChartConfig';
import { apiCallStatus } from 'helpers/constants';
import { loggerChartContainerId, DATA_LOGGER } from './constants';
import {
    PopOutButton,
    ResetZoomButton,
    withLeftButtonGroup,
    withPopOutRow,
    withRightButtonGroup
} from 'sharedComponents/Chart/headers';
import ErrorView from 'sharedComponents/ErrorBoundary/ErrorView';
import { useReportIssue } from 'sharedHooks/useReportIssue';
import './style.scss';
import useChartResize from 'sharedHooks/useChartResize';


const LoggerVisualisationComponent = ({
    model: {
        loggers,
        loggerId = [],
        loggersLoadingStatus,
        selectedLoggerSignals,
        signalDataLoadingStatus,
        errorData
    },
    actions: {
        getLoggers,
        getLoggerId,
        getEventLoggerSignals,
        removeSelectedSignal,
        resetLoggerData
    },
    selectedEvent
}) => {
    const { t: translate } = useTranslation();
    const { showReportIssue } = useReportIssue();
    const containerRef = useRef();
    const chartRef = useRef();

    useChartResize(containerRef, chartRef);

    const [selectedLogger, setSelectedLogger] = useState();
    const [selectedSignal, setSelectedSignal] = useState();

    const [isZoomed, setIsZoomed] = useState(false);
    const loggerData = false;

    useEffect(() => {
        if (loggerId && loggerId.length) {
            const isDataLogger = loggerId.some(logger => logger['type'] === DATA_LOGGER);
            if (isDataLogger) {
                getLoggers(loggerId);
            }
        }
    }, [loggerId, getLoggers]);

    useEffect(() => {
        if (loggers.length && loggersLoadingStatus === apiCallStatus.SUCCESS) {
            const firstLogger = loggers[0];
            setSelectedLogger(firstLogger);
            const signals = firstLogger.signals.slice(0, Math.min(firstLogger.signals.length, MAX_LOGGER_SIGNALS));
            getEventLoggerSignals(firstLogger.id, signals);
        }
    }, [loggersLoadingStatus]);

    useEffect(() => {
        if (loggers.length) {
            if (selectedLogger) {
                setSelectedLogger(find(loggers, { id: selectedLogger.id }));
            }
            if (selectedSignal) {
                setSelectedSignal(null);
            }
        }
    }, [loggers]);

    useEffect(() => {
        return () => {
            resetLoggerData();
        };
    }, []);

    useEffect(() => {
        if (selectedSignal) {
            const newSignals = [...selectedLoggerSignals];
            if (newSignals.length >= MAX_LOGGER_SIGNALS) {
                newSignals.pop();
            }
            getEventLoggerSignals(selectedLogger?.id, [selectedSignal]);
        }
    }, [selectedSignal]);

    const isLoading = loggersLoadingStatus === apiCallStatus.LOADING || signalDataLoadingStatus === apiCallStatus.LOADING;
    const chartConfig = useChartConfig(selectedLoggerSignals, removeSelectedSignal);

    if (errorData?.loadingStatus === apiCallStatus.ERROR) {
        const hasLoggerIds = Array.isArray(loggerId) && loggerId.length > 0;
        return <ErrorView
            errorCode={errorData.errorCode}
            onRetry={() => hasLoggerIds ? getLoggers(loggerId) : getLoggerId(selectedEvent?.rawEvent)}
            onReportIssue={showReportIssue}
        />;
    }

    const availableSignals = selectedLogger?.signals.filter(
        signal => !selectedLoggerSignals.some(s => s.signalID === signal.id)
    );

    const DataLoggerSelector = () => {
        return <Col xl={3} md={6}>
            <div className='data-logger-selector-label'>
                {translate('ABB.Powertrain.Frontend.eventListAssistanceSelectLogger')}
            </div>
            <GeneralSelect
                id='data-logger-selector'
                options={loggers}
                value={selectedLogger}
                getOptionLabel={option => option.label}
                getOptionValue={option => option.id}
                onChange={setSelectedLogger}
                isLoading={isLoading}
                isDisabled={isLoading}
                noOptionsMessage={() => translate('ABB.Powertrain.Frontend.lblNoOptions')}
            />
        </Col>;
    };

    const TrendSignalSelector = () => {
        return <Col xl={3} md={6}>
            <div className='trend-signal-selector-label'>
                {translate('ABB.Powertrain.Frontend.eventListAssistanceTrendSelectSignal')}
            </div>
            <GeneralSelect
                id='trend-signal-selector'
                placeholder={translate('ABB.Powertrain.Frontend.eventListAssistanceTrendSelectSignal')}
                options={availableSignals}
                value={selectedSignal}
                onChange={setSelectedSignal}
                isLoading={isLoading}
                isDisabled={isLoading}
                noOptionsMessage={() => translate('ABB.Powertrain.Frontend.lblNoOptions')}
            />
        </Col>;
    };

    const MainChartHeader = withPopOutRow([
        withLeftButtonGroup([DataLoggerSelector, TrendSignalSelector]),
        withRightButtonGroup([ResetZoomButton, PopOutButton])
    ]);

    const PopOutChartHeader = withPopOutRow([
        withRightButtonGroup([ResetZoomButton, PopOutButton])
    ]);

    return <Container fluid id='assistance-logger-visualisation-container'>
        <Row className='assistance-trend-chart'>
            {
                <Col >
                    <div ref={containerRef}>
                        <PopOutChart
                            hasKpi={!!selectedLoggerSignals.length}
                            data={loggerData}
                            renderNoKpi={() => <InfoMessage
                                title='ABB.Powertrain.Frontend.eventListAssistanceNoSignalSelected' />}
                            options={chartConfig}
                            loadingStatus={signalDataLoadingStatus}
                            container={loggerChartContainerId}
                            modules={chartPlugins}
                            isZoomed={isZoomed}
                            setIsZoomed={setIsZoomed}
                            mainChartHeader={MainChartHeader}
                            popOutChartHeader={PopOutChartHeader}
                            chartRef={chartRef}
                        />
                    </div>
                </Col>
            }
        </Row>
    </Container>;
};

LoggerVisualisationComponent.propTypes = {
    selectedAsset: PropTypes.shape({
        id: PropTypes.string, // Asset ID
        name: PropTypes.string, // Asset name
    }), // Selected asset details
    selectedEvent: PropTypes.shape({
        id: PropTypes.string, // Event ID
        rawEvent: PropTypes.object,
        info: PropTypes.shape({
            hasDataLoggers: PropTypes.bool, // Whether the event has data loggers
            hasBinaryLogger: PropTypes.bool, // Whether the event has a binary logger
        }), // Event information
    }), // Selected event details
    model: PropTypes.shape({
        showUtcDates: PropTypes.bool, // Whether to show UTC dates
        loggers: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string, // Logger ID
                label: PropTypes.string, // Logger label
                signals: PropTypes.arrayOf(
                    PropTypes.shape({
                        id: PropTypes.string, // Signal ID
                        name: PropTypes.string, // Signal name
                    })
                ), // Array of signals
            })
        ), // Array of loggers
        loggerId: PropTypes.arrayOf( // Added validation for loggerId
            PropTypes.shape({
                type: PropTypes.string, // Logger type
            })
        ), // Array of logger IDs
        loggersLoadingStatus: PropTypes.string, // Loading status for loggers
        selectedLoggerSignals: PropTypes.arrayOf(
            PropTypes.shape({
                signalID: PropTypes.string, // Selected signal ID
                name: PropTypes.string, // Selected signal name
            })
        ), // Array of selected logger signals
        signalDataLoadingStatus: PropTypes.string, // Loading status for signal data
        errorData: PropTypes.shape({
            loadingStatus: PropTypes.string,
            errorCode: PropTypes.number,
        }),
    }), // Model containing logger and signal data
    actions: PropTypes.shape({
        removeSelectedSignal: PropTypes.func, // Function to remove a selected signal
        getLoggers: PropTypes.func, // Function to fetch loggers
        getLoggerId: PropTypes.func,
        getEventLoggerSignals: PropTypes.func, // Function to fetch logger signals for an event
        resetLoggerData: PropTypes.func, // Function to reset logger data
    }), // Actions object containing functions
};

export default LoggerVisualisationComponent;
